import { useState, useRef, useCallback, useEffect } from 'react';
import { saveChatWidgetPosition, loadChatWidgetPosition } from './cookies';

interface DraggableOptions {
  elementWidth?: number;
  padding?: number;
  dragThreshold?: number;
  onPositionChange?: (position: { x: number }) => void;
  persistPosition?: boolean;
}

interface DraggableReturn {
  isDragging: boolean;
  wasDragged: boolean;
  position: { x: number };
  containerRef: React.RefObject<HTMLDivElement>;
  dragHandlers: {
    onMouseDown: (e: React.MouseEvent) => void;
    onTouchStart: (e: React.TouchEvent) => void;
  };
  getContainerStyle: () => React.CSSProperties;
  getDragElementStyle: () => React.CSSProperties;
  isNearLeftEdge: () => boolean;
}

export const useDraggable = (options: DraggableOptions = {}): DraggableReturn => {
  const {
    elementWidth = 48,
    padding = 20,
    dragThreshold = 5,
    onPositionChange,
    persistPosition = true
  } = options;

  const [isDragging, setIsDragging] = useState(false);
  const [wasDragged, setWasDragged] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0 });
  const [position, setPosition] = useState(() => {
    // Load saved position on initialization
    if (persistPosition) {
      const savedPosition = loadChatWidgetPosition();
      return savedPosition || { x: 0 }; // 0 means default right position
    }
    return { x: 0 };
  });
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });
  
  const containerRef = useRef<HTMLDivElement>(null);

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setWasDragged(false);
    setStartPosition({ x: e.clientX, y: e.clientY });
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left
      });
    }
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    
    e.preventDefault();
    
    // Check if we've moved beyond the drag threshold
    const deltaX = Math.abs(e.clientX - startPosition.x);
    const deltaY = Math.abs(e.clientY - startPosition.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    if (distance > dragThreshold && !wasDragged) {
      setWasDragged(true);
    }
    
    // Only update position if we've exceeded the threshold
    if (wasDragged || distance > dragThreshold) {
      const windowWidth = window.innerWidth;
      
      // Calculate new position
      const newX = e.clientX - dragOffset.x;
      
      // Apply constraints: keep padding from both edges
      const minX = padding;
      const maxX = windowWidth - elementWidth - padding;
      
      const constrainedX = Math.max(minX, Math.min(maxX, newX));
      
      // Convert to position relative to right edge (for consistency with current styling)
      const rightPosition = windowWidth - constrainedX - elementWidth;
      
      const newPosition = { x: rightPosition };
      setPosition(newPosition);
      onPositionChange?.(newPosition);
      
      // Save position to localStorage if persistence is enabled
      if (persistPosition) {
        saveChatWidgetPosition(newPosition);
      }
    }
  }, [isDragging, dragOffset.x, elementWidth, padding, onPositionChange, startPosition, dragThreshold, wasDragged]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    // Reset wasDragged after a short delay to allow click handlers to check it first
    setTimeout(() => {
      setWasDragged(false);
    }, 0);
  }, []);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setWasDragged(false);
    
    const touch = e.touches[0];
    setStartPosition({ x: touch.clientX, y: touch.clientY });
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: touch.clientX - rect.left
      });
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging || !containerRef.current) return;
    
    e.preventDefault();
    
    const touch = e.touches[0];
    
    // Check if we've moved beyond the drag threshold
    const deltaX = Math.abs(touch.clientX - startPosition.x);
    const deltaY = Math.abs(touch.clientY - startPosition.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    
    if (distance > dragThreshold && !wasDragged) {
      setWasDragged(true);
    }
    
    // Only update position if we've exceeded the threshold
    if (wasDragged || distance > dragThreshold) {
      const windowWidth = window.innerWidth;
      
      // Calculate new position
      const newX = touch.clientX - dragOffset.x;
      
      // Apply constraints: keep padding from both edges
      const minX = padding;
      const maxX = windowWidth - elementWidth - padding;
      
      const constrainedX = Math.max(minX, Math.min(maxX, newX));
      
      // Convert to position relative to right edge (for consistency with current styling)
      const rightPosition = windowWidth - constrainedX - elementWidth;
      
      const newPosition = { x: rightPosition };
      setPosition(newPosition);
      onPositionChange?.(newPosition);
      
      // Save position to localStorage if persistence is enabled
      if (persistPosition) {
        saveChatWidgetPosition(newPosition);
      }
    }
  }, [isDragging, dragOffset.x, elementWidth, padding, onPositionChange, startPosition, dragThreshold, wasDragged]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    // Reset wasDragged after a short delay to allow click handlers to check it first
    setTimeout(() => {
      setWasDragged(false);
    }, 0);
  }, []);

  // Add event listeners for mouse and touch events
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

  // Helper functions
  const getContainerStyle = useCallback((): React.CSSProperties => ({
    right: position.x === 0 ? `${padding}px` : `${position.x}px`,
    cursor: isDragging ? 'grabbing' : 'default'
  }), [position.x, isDragging, padding]);

  const getDragElementStyle = useCallback((): React.CSSProperties => ({
    cursor: isDragging ? 'grabbing' : 'grab'
  }), [isDragging]);

  const isNearLeftEdge = useCallback((): boolean => {
    const windowWidth = window.innerWidth;
    // When position.x === 0, button is at default right position (padding from right edge)
    // When position.x > 0, button is at custom position (position.x from right edge)
    const buttonPosition = position.x === 0 ? windowWidth - elementWidth - padding : windowWidth - position.x - elementWidth;
    return buttonPosition < windowWidth / 2;
  }, [position.x, elementWidth, padding]);

  return {
    isDragging,
    wasDragged,
    position,
    containerRef,
    dragHandlers: {
      onMouseDown: handleMouseDown,
      onTouchStart: handleTouchStart
    },
    getContainerStyle,
    getDragElementStyle,
    isNearLeftEdge
  };
};